import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Loader2, Star, MessageSquare } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import api from "../services/api";
import { formatLagosDateTime } from "../utils/tripLog";

// All data comes from the backend (the "bucket"). Nothing is hardcoded.

const formatDate = (d) =>
  formatLagosDateTime(d, "en-NG", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const FILTERS = [
  { key: "all", label: "All ratings", params: {} },
  // The reason this screen exists: a rider collecting one-stars used to be
  // invisible until somebody phoned in about it.
  { key: "poor", label: "Poor (1–2★)", params: { maxRating: 2 } },
  { key: "comments", label: "With comments", params: { withCommentOnly: true } },
];

const Stars = ({ value }) => (
  <span className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map((n) => (
      <Star
        key={n}
        className={`w-3.5 h-3.5 ${
          n <= value ? "fill-amber-400 text-amber-400" : "text-gray-300"
        }`}
      />
    ))}
  </span>
);

const Ratings = () => {
  const [filter, setFilter] = useState("all");
  const [ratings, setRatings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const active = FILTERS.find((f) => f.key === filter) || FILTERS[0];
      const qs = new URLSearchParams({ limit: "200", ...active.params });
      const { data } = await api.get(`/v1/admin/ratings?${qs.toString()}`);
      setRatings(Array.isArray(data) ? data : data?.data || []);
    } catch (error) {
      // An error is not "no ratings": clear the list and say so.
      setRatings([]);
      setLoadError(error?.response?.data?.message || "Couldn't load ratings.");
      toast.error(error?.response?.data?.message || "Failed to load ratings");
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const average =
    ratings.length > 0
      ? (
          ratings.reduce((sum, r) => sum + (r.rating || 0), 0) / ratings.length
        ).toFixed(2)
      : "—";

  return (
    <div className="flex min-h-screen bg-gray-50 dark:bg-gray-900">
      <Sidebar />
      <div className="flex-1 flex flex-col w-full lg:pl-64">
        <Topbar />
        <main className="flex-1 overflow-y-auto p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Ratings
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Individual ride ratings and the comments customers left.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                Showing
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {loadError ? "—" : ratings.length}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                Average (of shown)
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {average}
              </p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">
                With a comment
              </p>
              <p className="text-2xl font-semibold text-gray-900 dark:text-white">
                {loadError ? "—" : ratings.filter((r) => r.comment && r.comment.trim()).length}
              </p>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 rounded-lg text-sm ${
                  filter === f.key
                    ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900"
                    : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-x-auto">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : loadError ? (
              <div className="p-10 text-center text-sm text-red-600">
                {"Couldn't load ratings. "}
                <button type="button" onClick={load} className="underline font-medium">
                  Retry
                </button>
              </div>
            ) : ratings.length === 0 ? (
              <div className="p-10 text-center text-sm text-gray-500 dark:text-gray-400">
                <MessageSquare className="w-6 h-6 mx-auto mb-2 text-gray-400" />
                No ratings match this filter.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/40 text-left text-gray-500 dark:text-gray-400">
                  <tr>
                    <th className="px-4 py-3 font-medium">When</th>
                    <th className="px-4 py-3 font-medium">Rider</th>
                    <th className="px-4 py-3 font-medium">Customer</th>
                    <th className="px-4 py-3 font-medium">Rating</th>
                    <th className="px-4 py-3 font-medium">Comment</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {ratings.map((r) => (
                    <tr
                      key={r._id}
                      className={r.rating <= 2 ? "bg-red-50/50 dark:bg-red-900/10" : ""}
                    >
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                        {formatDate(r.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-gray-900 dark:text-white">
                        {r.riderId?.name || "—"}
                        <span className="ml-2 font-mono text-xs text-gray-500">
                          {r.riderId?.zoomaId || r.riderId?.riderId || ""}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-300">
                        {r.customerId?.name || "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <Stars value={r.rating} />
                      </td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-200 max-w-md">
                        {r.comment?.trim() ? (
                          r.comment
                        ) : (
                          <span className="text-gray-400 italic">
                            No comment
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Ratings;
